import IconButton from "@mui/material/IconButton";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { TranslateIcon } from "@phosphor-icons/react/dist/csr/Translate";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import styles from "./header.module.scss";
import { useAppSelector } from "@/redux/store.ts";
import showUserMessage from "../../utils/showUserMessage.ts";
import cn from "classnames";
import Menu from "@mui/material/Menu";

const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { serviceMetadata, currentService } = useAppSelector(state => state.layout);

  const close = () => {
    buttonRef.current?.focus();
    setAnchorEl(null);
    setOpen(false);
  };

  const handleClose = (e: Event) => {
    const path = e.composedPath();

    if (!dropdownRef.current || !buttonRef.current) {
      return;
    }

    if (path.includes(dropdownRef.current) || path.includes(buttonRef.current)) {
      return;
    }

    close();
  };

  useEffect(() => {
    window.addEventListener("click", handleClose);

    return () => {
      window.removeEventListener("click", handleClose);
    };
  }, []);

  const handleClick = () => {
    if (!open) {
      setAnchorEl(buttonRef.current);
      setOpen(true);
    } else {
      setAnchorEl(null);
      setOpen(false);
    }
  };

  const isServiceSupportLanguage = (language: string) => {
    if (serviceMetadata) {
      return serviceMetadata.supportedLanguages?.includes(language);
    }
    return false;
  };

  const changeLanguage = async (language: string) => {
    window.dispatchEvent(new CustomEvent("framework-set-loader", { detail: true }));

    const action = Promise.all([i18n.changeLanguage(language), new Promise(resolve => setTimeout(resolve, 1000))]);
    await action;
    window.dispatchEvent(new CustomEvent("OSTS_LANGUAGE_CHANGED", { detail: language }));

    if (!!currentService && !isServiceSupportLanguage(language)) {
      showUserMessage(t("languageSelector.notSupported"), "warning");
    }

    close();

    window.dispatchEvent(new CustomEvent("framework-set-loader", { detail: false }));
  };

  const getLanguage = () => {
    return i18n.language.split("-")[0];
  };

  const getSortedLanguages = () => {
    const currentLang = getLanguage();
    const languages = [
      { code: "en", label: "EN - English" },
      { code: "de", label: "DE - Deutsch (German)" },
      { code: "uk", label: "UK - Українська (Ukrainian)" },
    ];

    return languages.sort((a, b) => {
      if (a.code === currentLang) return -1;
      if (b.code === currentLang) return 1;
      return a.label.localeCompare(b.label);
    });
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={handleClick}
        className={cn(styles.languageSelector, { [styles.languageSelectorActive]: open })}
      >
        <TranslateIcon size={24} className={styles.languageIcon} />{" "}
        <span className={styles.languageText}>{getLanguage()}</span>
      </IconButton>

      <Menu anchorEl={anchorEl} id="languge-menu" open={open} disablePortal>
        <div ref={dropdownRef} className={styles.languageDropdownContainer}>
          <div className={styles.languageHeader}>{t("languageSelector.title")}</div>
          <div className={styles.languageDropdown}>
            {getSortedLanguages().map((lang, i) => (
              <MenuItem key={lang.code} onClick={i !== 0 ? () => changeLanguage(lang.code) : undefined}>
                {lang.label}
                {i === 0 && <CheckIcon weight="bold" size={16} />}
              </MenuItem>
            ))}
          </div>
        </div>
      </Menu>
    </>
  );
};

export default LanguageSelector;
